import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustom, useCustomMutation } from "@refinedev/core";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiUrlV2 } from "@/consts";
import { openLiveHost } from "@/utils/openLiveHost";
import { fmtWallDate, fmtWallTime } from "./time";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const GROUP_LABELS: Record<string, string> = {
  academy: "Academy",
  foundation: "Foundation",
  development: "Development",
  performance: "Performance",
};

type ScSession = {
  id: string;
  title: string | null;
  // Every group the class runs for.
  targetIDs: string[];
  startsAt: string;
  endsAt: string;
  seriesID: string | null;
  status: "SCHEDULED" | "CANCELLED";
};

export function ScScheduleList() {
  const navigate = useNavigate();
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf("day"),
    dayjs().add(8, "week").endOf("day"),
  ]);

  const from = range[0].toISOString();
  const to = range[1].toISOString();

  const { data, isLoading, refetch } = useCustom<{ data: ScSession[] }>({
    url: `${apiUrlV2}/schedule/admin/sc`,
    method: "get",
    config: { query: { from, to } },
  });
  const rows = data?.data?.data ?? [];

  const { mutate: removeMutate } = useCustomMutation();
  const { mutate: startLive } = useCustomMutation();
  const [startingId, setStartingId] = useState<string | null>(null);

  // GL-000: mint (or reuse) the SC live row for this occurrence and open the
  // host broadcast view. Athletes in the target groups then see a Join button.
  function goLive(session: ScSession) {
    setStartingId(session.id);
    startLive(
      {
        url: `${apiUrlV2}/live`,
        method: "post",
        values: { liveType: "SC", scheduleSessionID: session.id, liveStatus: "STARTED" },
      },
      {
        onSuccess: (res: any) => {
          const room = res?.data?.data?.roomID;
          if (!room) {
            message.error("Live started but no room id was returned");
            return;
          }
          openLiveHost({
            roomID: room,
            liveID: res?.data?.data?.id,
            hostName: session.title || "S&C",
          });
          message.success("Live started — opening host view");
        },
        onError: (err: any) =>
          message.error(err?.response?.data?.error || err?.message || "Could not start live"),
        onSettled: () => setStartingId(null),
      },
    );
  }

  function remove(session: ScSession, scope: "this" | "all_future") {
    const qs = scope === "all_future" ? "?scope=all_future" : "?scope=this";
    removeMutate(
      { url: `${apiUrlV2}/schedule/admin/sc/${session.id}${qs}`, method: "delete", values: {} },
      {
        onSuccess: () => {
          message.success(scope === "all_future" ? "Series ended" : "Session cancelled");
          refetch();
        },
        onError: (err: any) =>
          message.error(err?.response?.data?.error || err?.message || "Could not delete"),
      },
    );
  }

  const columns = [
    {
      title: "Date",
      dataIndex: "startsAt",
      render: (v: string) => fmtWallDate(v),
      sorter: (a: ScSession, b: ScSession) => a.startsAt.localeCompare(b.startsAt),
      defaultSortOrder: "ascend" as const,
    },
    {
      title: "Time",
      render: (_: any, r: ScSession) => `${fmtWallTime(r.startsAt)}–${fmtWallTime(r.endsAt)}`,
    },
    {
      title: "Groups",
      dataIndex: "targetIDs",
      render: (groups: string[]) =>
        groups.length > 0 ? (
          <Space size={4} wrap>
            {groups.map((g) => (
              <Tag key={g} color="purple">
                {GROUP_LABELS[g] ?? g}
              </Tag>
            ))}
          </Space>
        ) : (
          <Tag>—</Tag>
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (t: string | null) => t || <Text type="secondary">—</Text>,
    },
    {
      title: "Recurrence",
      dataIndex: "seriesID",
      render: (s: string | null) =>
        s ? <Tag color="blue">Recurring</Tag> : <Tag>One-off</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s: string) =>
        s === "CANCELLED" ? <Tag color="red">Cancelled</Tag> : <Tag color="green">Scheduled</Tag>,
    },
    {
      title: "Actions",
      render: (_: any, r: ScSession) => (
        <Space>
          <Button
            size="small"
            danger
            type="primary"
            loading={startingId === r.id}
            disabled={r.status === "CANCELLED"}
            onClick={() => goLive(r)}
          >
            ● Go live
          </Button>
          <Button size="small" onClick={() => navigate(`/sc-schedule/edit/${r.id}`)}>
            Edit
          </Button>
          <Popconfirm
            title="Cancel this session?"
            onConfirm={() => remove(r, "this")}
            okText="Cancel session"
          >
            <Button size="small" danger>
              Cancel
            </Button>
          </Popconfirm>
          {r.seriesID && (
            <Popconfirm
              title="End the whole series?"
              description="Removes this and all future occurrences."
              onConfirm={() => remove(r, "all_future")}
              okText="End series"
            >
              <Button size="small">End series</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          S&amp;C Schedule
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/sc-schedule/create")}>
          Create S&amp;C
        </Button>
      </Space>

      <Space style={{ marginBottom: 16 }}>
        <Text type="secondary">Showing</Text>
        <RangePicker
          value={range}
          allowClear={false}
          onChange={(v) => v && v[0] && v[1] && setRange([v[0], v[1]])}
        />
      </Space>

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: <Empty description="No S&C sessions in this range" /> }}
      />
    </Card>
  );
}
